/**
 * The single mapping from a sealed typed rejection (DOMAIN.md §C.6/§D.1) to what the journey does with
 * it — reused for BOTH the availability READ (`openSlots` returns a `rejection` code in its 200 body)
 * and the create-path 4xx problem (`bookingRejectionOf`). Every "the slot went away" rejection lands
 * on fresh alternatives, never a dead end (R7/R8); `BannedUser` is the neutral blocked screen with NO
 * blacklist vocabulary anywhere (R5, GU-4); online-off / not-live fall back to venue contact.
 */

export type RejectionOutcome =
  | { kind: "alternatives" }
  | { kind: "blocked" }
  | { kind: "contact"; code: string }
  | { kind: "inline"; code: string };

export function classifyRejection(code: string): RejectionOutcome {
  switch (code) {
    // The requested time is gone between search and submit — re-offer live alternatives (R7/R8).
    case "SLOT_UNAVAILABLE":
    case "COVERS_CAP_REACHED":
    case "OVERLAPPING_RESERVATION":
      return { kind: "alternatives" };

    // GU-4/BK-9: the neutral generic refusal — the ONLY thing a banned diner ever sees.
    case "BOOKING_NOT_POSSIBLE":
      return { kind: "blocked" };

    // Venue can't take online bookings right now / no online table fits — hand off to contact.
    case "ONLINE_BOOKING_DISABLED":
    case "VENUE_NOT_LIVE":
    case "NO_FIT_FOR_PARTY":
      return { kind: "contact", code };

    // Read-time date rejections — an inline note on the current step, the diner just picks another date.
    case "LEAD_TIME_VIOLATED":
    case "HORIZON_EXCEEDED":
    case "OUTSIDE_OPENING_HOURS":
      return { kind: "inline", code };

    default:
      return { kind: "inline", code };
  }
}

/** The catalog key for an inline / contact rejection message. Never leaks a blacklist hint (R5). */
export function rejectionMessageKey(code: string): string {
  switch (code) {
    case "TEMPORARILY_BLOCKED":
      return "widget.booking.reject.temporarilyBlocked";
    case "LEAD_TIME_VIOLATED":
      return "widget.booking.reject.leadTime";
    case "HORIZON_EXCEEDED":
      return "widget.booking.reject.horizon";
    case "OUTSIDE_OPENING_HOURS":
      return "widget.booking.reject.closed";
    case "ONLINE_BOOKING_DISABLED":
      return "widget.booking.reject.disabled";
    case "VENUE_NOT_LIVE":
      return "widget.booking.reject.notLive";
    case "NO_FIT_FOR_PARTY":
      return "widget.booking.reject.noFit";
    case "SLOT_UNAVAILABLE":
    case "COVERS_CAP_REACHED":
    case "OVERLAPPING_RESERVATION":
      return "widget.booking.reject.taken";
    default:
      return "widget.booking.reject.generic";
  }
}
